import { useRef, useState } from "react";
import { useLang } from "@/lib/lang";
import { uploadDocument } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, FileImage, FileType2, Loader2 } from "lucide-react";

const ACCEPT = ".pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg,.webp,.bmp,.tiff";
const MAX_BYTES = 50 * 1024 * 1024;

export default function UploadDropzone({ onUploaded, compact = false, uploadFn }) {
  const { t, lang } = useLang();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const doUpload = uploadFn || uploadDocument;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const oversized = files.find((f) => f.size > MAX_BYTES);
    if (oversized) {
      toast.error(
        lang === "ar"
          ? `الملف "${oversized.name}" يتجاوز الحد المسموح (50 ميجابايت)`
          : `"${oversized.name}" exceeds the 50MB limit`,
      );
      return;
    }
    setIsUploading(true);
    setProgress(0);
    try {
      for (const file of files) {
        const meta = await doUpload(file, setProgress);
        toast.success(`${t("upload_success")}: ${meta.filename}`);
        onUploaded?.(meta);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || t("upload_fail");
      toast.error(msg);
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div
      data-testid="upload-dropzone"
      onClick={() => !isUploading && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
      className={[
        "dropzone cursor-pointer",
        compact ? "p-5" : "p-10",
        isDragging ? "active" : "",
        isUploading ? "pointer-events-none opacity-90" : "",
      ].join(" ")}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        hidden
        data-testid="upload-input"
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
      />
      <div className="flex flex-col items-center justify-center text-center gap-3">
        {isUploading ? (
          <>
            <Loader2 className="animate-spin text-slate-900" size={compact ? 22 : 32} />
            <div className="text-sm text-slate-600">{t("uploading")}</div>
            {progress > 0 && (
              <div className="w-48 h-1 bg-slate-200">
                <div className="h-full bg-slate-900 transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 text-slate-400">
              <FileText size={compact ? 18 : 24} strokeWidth={1.4} />
              <FileType2 size={compact ? 18 : 24} strokeWidth={1.4} />
              <FileImage size={compact ? 18 : 24} strokeWidth={1.4} />
            </div>
            <div className="flex items-center gap-2 text-slate-900">
              <Upload size={compact ? 14 : 16} />
              <span className={compact ? "text-sm font-medium" : "text-base font-medium"}>
                {t("drop_here")}
              </span>
            </div>
            <div className="eyebrow !text-[10px]">{t("supported")}</div>
          </>
        )}
      </div>
    </div>
  );
}
